import React, { useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import ViewByUsers from "./ViewByUsers";
import AssignScorecard from "./AssignScorecard";
import CustomSearchFieldTextInput from "../../../components/custom/form/CustomSearchFieldTextInput";

import autoqarulesService from "../../../services/autoqarules.service";
import {
  AutoQARule,
  UserAllocatedAutoQARule,
} from "../../../types/automatedQA/AutomatedQA";
import { CallResult } from "../../../types/call";
import callResultService from "../../../services/callResult.service";
import scorecardService from "../../../services/scorecard.service";
import { Scorecard } from "../../../types/scorecard";
import { AxiosError } from "axios";
import { GridExpandChangeEvent, GridPageChangeEvent } from "@progress/kendo-react-grid";
import useTranslation from "../../../hooks/useTranslation";
import useLocale from "../../../hooks/useLocale";
import { Link } from "react-router-dom";
import "./Scorecards.scss";
import { Tooltip } from "@progress/kendo-react-tooltip";

export interface PageDataProps{
  skip:number;
  take:number;
}

const AutomatedQA: React.FC = () => {
  const trans=useTranslation("AutomatedQA");
  const localeCtx = useLocale();
  const [view, setView] = useState<"autoqa-list"|"assign-scorecard">("autoqa-list");
  const [search, setSearch] = useState<string>("");
  const [automatedQARules, setAutomatedQARules] = useState<AutoQARule[]>([]);
  const [userAllocatedAutoQARules, setUserAllocatedQARules] = useState<UserAllocatedAutoQARule[]>([]);
  const [callResults, setCallResults] = useState<CallResult[]>([]);
  const [scorecards, setScorecards] = useState<Scorecard[]>([]);
  const [updateUserAllocatedAutoQARulesData,setUpdateUserAllocatedAutoQARulesData]=useState<UserAllocatedAutoQARule>();
  const [parentLoader,setParentLoader]=useState<boolean>(false);
  const [error,setError]=useState<string>("");
  const [page, setPage] = useState<PageDataProps>({
    take:10,
    skip:0
  })

  useEffect(() => {
    if (
      !localeCtx?.selectedLocale?.current.componentTranslations[
        "AutomatedQA"
      ]
    ) {
      trans.fetchTranslations("AutomatedQA");
    }
  }, [localeCtx?.selectedLocale]);  

  useEffect(() => {
    getAllCallResults();
    getAllScorecards();
  }, []);

  useEffect(() => {
    fetchAllAutoQARules();
    fetchAllUserAllocatedAutoQARules();
  }, []);

  const fetchAllAutoQARules = async () => {
    setParentLoader(true);
    try {
      const data = await autoqarulesService.fetchAllAutoQARules();
      setAutomatedQARules(data);
    } catch (error) {
      setAutomatedQARules([]);
      setParentLoader(false);
    }
  };

  const fetchAllUserAllocatedAutoQARules = async (appendLastData?:UserAllocatedAutoQARule) => {
    setError("");
    setParentLoader(true);
    try {
      const data = await autoqarulesService.fetchAllUserAllocatedAutoQARules();
      let filteredData = data.filter((d:UserAllocatedAutoQARule)=>d.autoQARules.length > 0 || d.defaultScoreCard);
      if(appendLastData){
        filteredData = filteredData.filter((item:UserAllocatedAutoQARule)=>item.user.id !== appendLastData.user.id);
        filteredData = [...filteredData,appendLastData]
      }
      setUserAllocatedQARules(filteredData);
    } catch (error) {
      if (error instanceof AxiosError){
        setError(error.message ?? trans.fetchLabelKeyTranslation("SomethingWentWrong","Something went wrong"))
      }
    } finally{
      setParentLoader(false);
    }
  };

  const getAllCallResults = async () => {
    try {
      const response = await callResultService.getAllCallResults();
      setCallResults(
        response.filter((res: CallResult) => res.isArchived !== true)
      );
    } catch (err) {
      setCallResults([])
    }
  };

  const getAllScorecards = async () => {
    try {
      const response = await scorecardService.fetchAllScorecards();
      setScorecards(
        response.filter((res: Scorecard) => res.isArchived !== true)
      );
    } catch (err) {
      setScorecards([])
    } 
  };

  const onSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setPage({
      take:10,
      skip:0
    })
    setSearch(e.currentTarget.value);
  };

  const filterUserAllocatedRulesBasedOnSearch=(userAllocRule:UserAllocatedAutoQARule)=>{
    var seachLower = search.toLowerCase();
    const userMail = userAllocRule.user.email.toLowerCase();
    const userName = (userAllocRule.user.firstName + ' ' + userAllocRule.user.lastName).toLowerCase();
    const scoreCardName = userAllocRule.defaultScoreCard?.scorecardName.toLowerCase();
    if(userMail.includes(seachLower) || userName.includes(seachLower) || scoreCardName?.includes(seachLower)
    || userAllocRule.autoQARules.some((x) => { return x.callResult.text.toLowerCase().includes(seachLower) || 
        x.reasonCodes.some((y) => { return y.name.toLowerCase().includes(seachLower) ||
        x.scoreCard.scorecardName.toLowerCase().includes(seachLower)})})){
      return true;
    }
    return false;
  }

   const handlePageChange = (event: GridPageChangeEvent) => {
    setPage(event.page);
    };

    const handleExpandChange = (e: GridExpandChangeEvent) => {
      const updatedExpandedAutoQARules = automatedQARules.map((item:AutoQARule) =>
        item.id === e.dataItem.id
          ? { ...item, expanded: !item.expanded }
          : { ...item, expanded:false}
      );
      setAutomatedQARules(updatedExpandedAutoQARules);
    };

  return (
    <div>
      <div className="callListInner">
        <div className="callListBox">
          <div className="row">
            <div className="col-md-12">
              <div className="row">
                <div className="col-md-12">
                  <div className="card cardEffect cardMobile">
                    <div className="card-header d-flex justify-content-between p-0 p-b-12">
                      <span className="fs-13 font-weight-semi d-flex align-items-center">
                        {trans.fetchLabelKeyTranslation("AutomatedQA","Auto QA")}
                      </span>
                      <div className="buttons-container hov-transparent d-flex align-itmes-center">
                       {view === "autoqa-list" && (
                        <div className="searchBox searchCol m-r-10">
                          <CustomSearchFieldTextInput
                            className="input-search"
                            placeholder={trans.fetchLabelKeyTranslation("Search","Search")}
                            value={search}
                            updateValue={setSearch}
                            searchTextChangeHandler={onSearchChange}
                            onEscapeKeyFunc={true}
                          />
                        </div>
                        )}
                        {view==="autoqa-list" && (
                          <Button
                            className={`btn bg-primary text-white fs-13 fw-normal`}
                            onClick={() => {
                              setSearch("");
                              setView("assign-scorecard");
                            }}
                            style={{ height: "28px" }}
                          >
                            {trans.fetchLabelKeyTranslation("SetAutoQATitle","Set Auto QA")}
                          </Button>
                        )}
                        {view==="assign-scorecard" && (
                          <Link
                            to={"#"}
                            onClick={() => {
                              setSearch("");
                              setView("autoqa-list");
                            }}
                            className="btn-link text-primary p-0 m-0 d-flex align-items-center"
                          >
                            <i className="bi bi-arrow-left fs-18 p-r-5 line-height-1"></i>
                            <span className="fs-13 font-weight-semi">
                                Back
                            </span>
                          </Link>
                        )}
                      </div>
                    </div>
                    <div className="card-body p-0">
                      <div className="autoqa-scroll">
                        {view === "autoqa-list" && (
                          <>
                            {!parentLoader && !error && userAllocatedAutoQARules.length===0 ? (
                              <>
                                <div className="text-center py-5 px-3 mx-auto" style={{maxWidth: '600px'}}>
                                  <h5 className="mb-4">{trans.fetchLabelKeyTranslation("NoAutoQATextOne","Start auto evaluating your calls")}</h5>
                                  <p className="mb-4">{trans.fetchLabelKeyTranslation("NoAutoQATextTwo","Auto evaluation uses AI to automatically analyze calls using scorecards mapped to outcomes and users.")}</p>
                                  <Button className={`btn bg-primary text-white fs-13 fw-normal px-4`}
                                    onClick={() => {setSearch(""); setView("assign-scorecard"); }}
                                    style={{ height: "40px" }} >
                                    {trans.fetchLabelKeyTranslation("NoAutoQATextThree","Set up auto evaluation")}
                                  </Button>
                                </div>                  
                              </>
                            ) : (
                              <ViewByUsers
                                userAllocatedAutoQARules={userAllocatedAutoQARules.filter((uaaqr:UserAllocatedAutoQARule)=>filterUserAllocatedRulesBasedOnSearch(uaaqr))}
                                setUpdateUserAllocatedAutoQARulesData={setUpdateUserAllocatedAutoQARulesData}
                                setView={setView}
                                view={view}
                                parentLoader={parentLoader}
                                error={error}
                                fetchAllAutoQARules={fetchAllAutoQARules}
                                fetchAllUserAllocatedAutoQARules={fetchAllUserAllocatedAutoQARules}
                                page={page}
                                handlePageChange={handlePageChange}
                                setPage={setPage}
                                setUserAllocatedQARules={setUserAllocatedQARules}
                              />
                            )}
                          </>
                        )}
                        {view === "assign-scorecard" && (
                          <Tooltip
                            anchorElement={"target"}
                            position={"top"}
                            parentTitle={true}
                          >
                            <AssignScorecard
                              callResults={callResults}
                              scorecards={scorecards}
                              automatedQARules={automatedQARules}   
                              setAutomatedQARules={setAutomatedQARules}                         
                              setView={setView}
                              view={view}
                              updateUserAllocatedAutoQARulesData={updateUserAllocatedAutoQARulesData}
                              setUpdateUserAllocatedAutoQARulesData={setUpdateUserAllocatedAutoQARulesData}
                              fetchAllUserAllocatedAutoQARules={fetchAllUserAllocatedAutoQARules}
                              userAllocatedAutoQARules={userAllocatedAutoQARules}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomatedQA;
